import { useState, useEffect, useCallback } from 'react';

interface UseReviewModalOptions<TStep extends string, TSuccessType extends string> {
  initialStep: TStep;
  initialSuccessType: TSuccessType;
  onClose: (open: boolean) => void;
}

interface UseReviewModalReturn<TStep extends string, TSuccessType extends string> {
  step: TStep;
  setStep: (step: TStep) => void;
  successType: TSuccessType;
  setSuccessType: (type: TSuccessType) => void;
  error: string | undefined;
  setError: (error: string | undefined) => void;
  handleFinish: () => void;
}

/**
 * Shared hook for managing review modal state
 * Handles step navigation, success types, error states, and modal closing
 * Resets to initial state when modal opens
 */
export function useReviewModal<TStep extends string, TSuccessType extends string>(
  open: boolean,
  options: UseReviewModalOptions<TStep, TSuccessType>
): UseReviewModalReturn<TStep, TSuccessType> {
  const { initialStep, initialSuccessType, onClose } = options;

  const [step, setStep] = useState<TStep>(initialStep);
  const [successType, setSuccessType] = useState<TSuccessType>(initialSuccessType);
  const [error, setError] = useState<string | undefined>();

  useEffect(
    function resetStepAndErrorWhenModalOpens() {
      if (open) {
        setStep(initialStep);
        setError(undefined);
      }
    },
    [open, initialStep]
  );

  // Handle modal close
  const handleFinish = useCallback(() => {
    onClose(false);
  }, [onClose]);

  return {
    step,
    setStep,
    successType,
    setSuccessType,
    error,
    setError,
    handleFinish
  };
}
